// frontend/src/lib/platformErrors.ts
//
// Single source of truth for all structured error codes passed between
// the parent frame and the mini-app iframe.
//
// Every error that can be sent via the llambada:error postMessage channel
// MUST be defined here. The iframe error handler dispatches on `code`,
// not on keyword-matching message strings.

// ---------------------------------------------------------------------------
// Error codes
// ---------------------------------------------------------------------------

export const ERROR_CODES = {
  // ── Limit errors ─────────────────────────────────────────────────────────
  // Show a modal with upgrade prompt
  RATE_LIMITED: 'RATE_LIMITED',
  DAILY_LIMIT_REACHED: 'DAILY_LIMIT_REACHED',
  MONTHLY_LIMIT_REACHED: 'MONTHLY_LIMIT_REACHED',
  STORAGE_LIMIT_EXCEEDED: 'STORAGE_LIMIT_EXCEEDED',
  MONTHLY_UPLOAD_LIMIT_EXCEEDED: 'MONTHLY_UPLOAD_LIMIT_EXCEEDED',
  SPONSORED_LIMIT_REACHED: 'SPONSORED_LIMIT_REACHED',
  GUEST_LIMIT_REACHED: 'GUEST_LIMIT_REACHED',
  GUEST_RATE_LIMITED: 'GUEST_RATE_LIMITED',
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',

  // ── Auth errors ──────────────────────────────────────────────────────────
  // Show a modal with auth / subscription prompt
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  SUBSCRIPTION_REQUIRED: 'SUBSCRIPTION_REQUIRED',
  TURNSTILE_REQUIRED: 'TURNSTILE_REQUIRED',
  TURNSTILE_FAILED: 'TURNSTILE_FAILED',

  // ── Execution errors ─────────────────────────────────────────────────────
  // Show a toast notification
  BACKEND_BUILDING: 'BACKEND_BUILDING',
  EXECUTION_TIMEOUT: 'EXECUTION_TIMEOUT',
  EXECUTION_FAILED: 'EXECUTION_FAILED',
  INPUT_TOO_LARGE: 'INPUT_TOO_LARGE',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  CODE_NOT_FOUND: 'CODE_NOT_FOUND',
  CODE_DOWNLOAD_FAILED: 'CODE_DOWNLOAD_FAILED',
  PROJECT_NOT_FOUND: 'PROJECT_NOT_FOUND',
  LLM_ERROR: 'LLM_ERROR',
  LLM_SERVICE_UNAVAILABLE: 'LLM_SERVICE_UNAVAILABLE',
  SUBMIT_FAILED: 'SUBMIT_FAILED',

  // ── Infrastructure errors ────────────────────────────────────────────────
  // Show a toast notification
  SERVER_MISCONFIGURED: 'SERVER_MISCONFIGURED',
  AUTH_INVALID: 'AUTH_INVALID',
  API_KEY_INVALID: 'API_KEY_INVALID',
  API_KEY_REQUIRED: 'API_KEY_REQUIRED',
  INVALID_REQUEST: 'INVALID_REQUEST',

  // ── Fallback ─────────────────────────────────────────────────────────────
  UNKNOWN: 'UNKNOWN',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// ---------------------------------------------------------------------------
// Categories – used by the iframe to decide *how* to display the error
// ---------------------------------------------------------------------------

export type ErrorCategory = 'limit' | 'auth' | 'execution' | 'infrastructure' | 'unknown';

export const ERROR_CATEGORY: Record<ErrorCode, ErrorCategory> = {
  // Limit
  [ERROR_CODES.RATE_LIMITED]: 'limit',
  [ERROR_CODES.DAILY_LIMIT_REACHED]: 'limit',
  [ERROR_CODES.MONTHLY_LIMIT_REACHED]: 'limit',
  [ERROR_CODES.STORAGE_LIMIT_EXCEEDED]: 'limit',
  [ERROR_CODES.MONTHLY_UPLOAD_LIMIT_EXCEEDED]: 'limit',
  [ERROR_CODES.SPONSORED_LIMIT_REACHED]: 'limit',
  [ERROR_CODES.GUEST_LIMIT_REACHED]: 'limit',
  [ERROR_CODES.GUEST_RATE_LIMITED]: 'limit',
  [ERROR_CODES.INSUFFICIENT_CREDITS]: 'limit',
  // Auth
  [ERROR_CODES.AUTH_REQUIRED]: 'auth',
  [ERROR_CODES.SUBSCRIPTION_REQUIRED]: 'auth',
  [ERROR_CODES.TURNSTILE_REQUIRED]: 'auth',
  [ERROR_CODES.TURNSTILE_FAILED]: 'auth',
  // Execution
  [ERROR_CODES.BACKEND_BUILDING]: 'execution',
  [ERROR_CODES.EXECUTION_TIMEOUT]: 'execution',
  [ERROR_CODES.EXECUTION_FAILED]: 'execution',
  [ERROR_CODES.INPUT_TOO_LARGE]: 'execution',
  [ERROR_CODES.FILE_TOO_LARGE]: 'execution',
  [ERROR_CODES.CODE_NOT_FOUND]: 'execution',
  [ERROR_CODES.CODE_DOWNLOAD_FAILED]: 'execution',
  [ERROR_CODES.PROJECT_NOT_FOUND]: 'execution',
  [ERROR_CODES.LLM_ERROR]: 'execution',
  [ERROR_CODES.LLM_SERVICE_UNAVAILABLE]: 'execution',
  [ERROR_CODES.SUBMIT_FAILED]: 'execution',
  // Infrastructure
  [ERROR_CODES.SERVER_MISCONFIGURED]: 'infrastructure',
  [ERROR_CODES.AUTH_INVALID]: 'infrastructure',
  [ERROR_CODES.API_KEY_INVALID]: 'infrastructure',
  [ERROR_CODES.API_KEY_REQUIRED]: 'infrastructure',
  [ERROR_CODES.INVALID_REQUEST]: 'infrastructure',
  // Unknown
  [ERROR_CODES.UNKNOWN]: 'unknown',
};

// ---------------------------------------------------------------------------
// Display metadata – used by the iframe modal / toast handlers
// ---------------------------------------------------------------------------

export interface ErrorDisplay {
  title: string;
  defaultMessage: string;
  /** Whether to show "Upgrade Your Plan" button */
  showUpgrade: boolean;
  /** Whether to show "Sign In" button */
  showSignIn: boolean;
}

export const ERROR_DISPLAY: Record<ErrorCode, ErrorDisplay> = {
  // ── Limit ────────────────────────────────────────────────────────────────
  [ERROR_CODES.RATE_LIMITED]: {
    title: 'Too Many Requests',
    defaultMessage: "You've made too many requests. Please wait a moment and try again.",
    showUpgrade: true,
    showSignIn: false,
  },
  [ERROR_CODES.DAILY_LIMIT_REACHED]: {
    title: 'Daily Limit Reached',
    defaultMessage: "You've reached your daily AI call limit. Try again tomorrow or upgrade for more.",
    showUpgrade: true,
    showSignIn: false,
  },
  [ERROR_CODES.MONTHLY_LIMIT_REACHED]: {
    title: 'Monthly Limit Reached',
    defaultMessage: "You've reached your monthly AI call limit. Upgrade your plan for more.",
    showUpgrade: true,
    showSignIn: false,
  },
  [ERROR_CODES.STORAGE_LIMIT_EXCEEDED]: {
    title: 'Storage Limit Reached',
    defaultMessage: "You've reached your storage limit. Upgrade your plan for more storage space.",
    showUpgrade: true,
    showSignIn: false,
  },
  [ERROR_CODES.MONTHLY_UPLOAD_LIMIT_EXCEEDED]: {
    title: 'Upload Limit Reached',
    defaultMessage: "You've reached your monthly upload limit. Upgrade your plan for more.",
    showUpgrade: true,
    showSignIn: false,
  },
  [ERROR_CODES.SPONSORED_LIMIT_REACHED]: {
    title: 'Free AI Calls Exhausted',
    defaultMessage: "You've used all your free AI calls for today. Add your own API key in Settings or upgrade for more.",
    showUpgrade: true,
    showSignIn: false,
  },
  [ERROR_CODES.GUEST_LIMIT_REACHED]: {
    title: 'Guest Limit Reached',
    defaultMessage: 'Guest AI capacity has been reached for this hour. Please sign in for continued access.',
    showUpgrade: false,
    showSignIn: true,
  },
  [ERROR_CODES.GUEST_RATE_LIMITED]: {
    title: 'Please Wait',
    defaultMessage: 'Please wait a few seconds before making another request. Sign in for faster access.',
    showUpgrade: false,
    showSignIn: true,
  },
  [ERROR_CODES.INSUFFICIENT_CREDITS]: {
    title: 'Insufficient Credits',
    defaultMessage: 'Not enough credits to complete this run. Please top up your credits and try again.',
    showUpgrade: true,
    showSignIn: false,
  },

  // ── Auth ──────────────────────────────────────────────────────────────────
  [ERROR_CODES.AUTH_REQUIRED]: {
    title: 'Sign In Required',
    defaultMessage: 'This app requires you to sign in to use it.',
    showUpgrade: false,
    showSignIn: true,
  },
  [ERROR_CODES.SUBSCRIPTION_REQUIRED]: {
    title: 'Subscription Required',
    defaultMessage: 'This app is available exclusively to paid subscribers.',
    showUpgrade: true,
    showSignIn: false,
  },
  [ERROR_CODES.TURNSTILE_REQUIRED]: {
    title: 'Verification Required',
    defaultMessage: 'Please complete the security verification to continue.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.TURNSTILE_FAILED]: {
    title: 'Verification Failed',
    defaultMessage: 'Security verification failed. Please refresh the page and try again.',
    showUpgrade: false,
    showSignIn: false,
  },

  // ── Execution ─────────────────────────────────────────────────────────────
  [ERROR_CODES.BACKEND_BUILDING]: {
    title: 'Backend Rebuilding',
    defaultMessage: 'The backend is currently rebuilding. Please wait 1–2 minutes and try again.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.EXECUTION_TIMEOUT]: {
    title: 'Execution Timed Out',
    defaultMessage: 'Your backend execution timed out. Upgrade your plan for longer runtime.',
    showUpgrade: true,
    showSignIn: false,
  },
  [ERROR_CODES.EXECUTION_FAILED]: {
    title: 'Execution Failed',
    defaultMessage: 'The backend process exited with an error. Check the logs for details.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.INPUT_TOO_LARGE]: {
    title: 'Input Too Large',
    defaultMessage: 'The combined input size exceeds the 50 MB limit. Please reduce the size of your files.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.FILE_TOO_LARGE]: {
    title: 'File Too Large',
    defaultMessage: 'The file is too large to upload. Please reduce the file size.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.CODE_NOT_FOUND]: {
    title: 'Code Not Found',
    defaultMessage: 'main.py was not found in the project code.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.CODE_DOWNLOAD_FAILED]: {
    title: 'Download Failed',
    defaultMessage: 'Failed to download the project code from storage.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.PROJECT_NOT_FOUND]: {
    title: 'Project Not Found',
    defaultMessage: 'The requested project could not be found.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.LLM_ERROR]: {
    title: 'AI Service Error',
    defaultMessage: 'The AI service encountered an error. Please try again.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.LLM_SERVICE_UNAVAILABLE]: {
    title: 'AI Service Unavailable',
    defaultMessage: 'The AI service is currently unavailable. Please try again later.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.SUBMIT_FAILED]: {
    title: 'Submit Failed',
    defaultMessage: 'Failed to submit your request. Please try again.',
    showUpgrade: false,
    showSignIn: false,
  },

  // ── Infrastructure ────────────────────────────────────────────────────────
  [ERROR_CODES.SERVER_MISCONFIGURED]: {
    title: 'Server Error',
    defaultMessage: 'The server is misconfigured. Please contact the app creator.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.AUTH_INVALID]: {
    title: 'Session Expired',
    defaultMessage: 'Your session has expired. Please refresh the page.',
    showUpgrade: false,
    showSignIn: true,
  },
  [ERROR_CODES.API_KEY_INVALID]: {
    title: 'Invalid API Key',
    defaultMessage: 'The configured API key is invalid. Please update it in Settings.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.API_KEY_REQUIRED]: {
    title: 'API Key Required',
    defaultMessage: 'An API key is required to use this feature. Please configure it in Settings.',
    showUpgrade: false,
    showSignIn: false,
  },
  [ERROR_CODES.INVALID_REQUEST]: {
    title: 'Invalid Request',
    defaultMessage: 'The request was invalid. Please try again.',
    showUpgrade: false,
    showSignIn: false,
  },

  // ── Unknown ───────────────────────────────────────────────────────────────
  [ERROR_CODES.UNKNOWN]: {
    title: 'Unknown Error',
    defaultMessage: 'An unexpected error occurred. Please contact support if this persists.',
    showUpgrade: false,
    showSignIn: false,
  },
};

// ---------------------------------------------------------------------------
// Structured error interface used by postMessage protocol
// ---------------------------------------------------------------------------

export interface PlatformError {
  code: ErrorCode;
  message: string;
  details?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Helper: build a PlatformError
// ---------------------------------------------------------------------------

export function makePlatformError(
  code: ErrorCode,
  message?: string,
  details?: Record<string, unknown>
): PlatformError {
  return {
    code,
    message: message || ERROR_DISPLAY[code]?.defaultMessage || 'An error occurred.',
    details,
  };
}

// ---------------------------------------------------------------------------
// Serialise the error catalog to plain JS for embedding in the iframe runtime.
// This avoids duplicating the catalog by hand inside the template string.
// ---------------------------------------------------------------------------

export function getErrorCatalogJS(): string {
  return `
    var __ERROR_CODES = ${JSON.stringify(ERROR_CODES)};
    var __ERROR_CATEGORY = ${JSON.stringify(ERROR_CATEGORY)};
    var __ERROR_DISPLAY = ${JSON.stringify(ERROR_DISPLAY)};
  `;
}